<script lang="ts">
    import { onDestroy, onMount } from "svelte"
    import { kirbyFetch } from "../../../../api/kirby/api"
    import MoreButton from "../../../widgets/MoreButton.svelte"
    import MedialibImage from "../../../widgets/MedialibImage.svelte"
    import Markdown from "../Markdown.svelte"
    import VideoJs from "./VideoJs.svelte"
    import { animateOnVisible } from "../../../../configs/config"
    import { parseSecondaryLink } from "../articleSlider/link-utils"
    let {
        block,
    }: {
        block: NumbersRowBlock
    } = $props()
    const cnt = block.content

    const toBoolean = (value: unknown): boolean => value === true || value === "true" || value === 1 || value === "1"

    const useImageSlider = $derived(toBoolean(cnt.use_image_slider))
    const sliderImages = $derived(
        useImageSlider && Array.isArray(cnt.slider_images)
            ? cnt.slider_images.filter((slide) => Array.isArray(slide?.image) && slide.image.length > 0)
            : []
    )
    const hasSlider = $derived(useImageSlider && sliderImages.length > 0)
    const hasMultipleSlides = $derived(sliderImages.length > 1)
    const sliderAutoplay = $derived(hasSlider && toBoolean(cnt.slider_autoplay ?? true))
    const sliderAutoplayInterval = $derived.by(() => {
        const parsed = Number(cnt.slider_autoplay_interval)
        return parsed && parsed >= 1000 ? parsed : 6000
    })

    let fileLink: string | undefined = $state()
    let videoFileRequestId = 0

    async function loadVideoFileLink(fileId?: string) {
        const requestId = ++videoFileRequestId
        if (useImageSlider || !fileId) {
            if (fileLink !== undefined) fileLink = undefined
            return
        }

        const res = await kirbyFetch<KirbyPage>({
            query: `file('${fileId}')`,
            select: { url: true },
        })

        if (requestId !== videoFileRequestId) return

        const nextFileLink = res?.url
        if (fileLink !== nextFileLink) fileLink = nextFileLink
    }

    $effect(() => {
        const fileId = Array.isArray(cnt.video_file) ? cnt.video_file[0] : undefined
        void loadVideoFileLink(fileId)
    })

    if (typeof window === "undefined") {
        const fileId = Array.isArray(cnt.video_file) ? cnt.video_file[0] : undefined
        void loadVideoFileLink(fileId)
    }

    let activeSlide = $state(0)
    let autoplayTimer: ReturnType<typeof setTimeout> | null = null

    function nextSlide(fromAutoplay = false) {
        if (!hasSlider) return
        activeSlide = (activeSlide + 1) % sliderImages.length
        if (!fromAutoplay) scheduleAutoplay()
    }

    function prevSlide() {
        if (!hasSlider) return
        activeSlide = (activeSlide - 1 + sliderImages.length) % sliderImages.length
        scheduleAutoplay()
    }

    function stopAutoplay() {
        if (autoplayTimer) {
            clearTimeout(autoplayTimer)
            autoplayTimer = null
        }
    }

    function scheduleAutoplay() {
        stopAutoplay()
        if (!sliderAutoplay || !hasMultipleSlides) return
        autoplayTimer = setTimeout(() => {
            nextSlide(true)
            scheduleAutoplay()
        }, sliderAutoplayInterval)
    }
    onMount(() => {
        animateOnVisible(".bold-numbers li", {
            translateY: ["100%", "0rem"],
            opacity: [0, 1],
            duration: 500,
            delay: (_element: Element, index: number) => index * 100,
            easing: "easeIn",
        })
    })

    onDestroy(() => stopAutoplay())
    $effect(() => {
        if (!hasSlider) {
            stopAutoplay()
            activeSlide = 0
            return
        }
        if (activeSlide >= sliderImages.length) {
            activeSlide = 0
        }
    })

    $effect(() => {
        if (!hasSlider || !hasMultipleSlides || !sliderAutoplay) {
            stopAutoplay()
            return
        }
        scheduleAutoplay()
    })
    function openLinkInNewTab(link: string) {
        if (link.startsWith("http")) {
            return true
        } else {
            return false
        }
    }

    const resolveTarget = (link?: string, target?: string) => {
        if (target) return target
        if (!link) return "_self"
        return openLinkInNewTab(link) ? "_blank" : "_self"
    }
</script>

<section class="video-text-row">
    <div class="media-card" class:slider={hasSlider}>
        {#if hasSlider}
            <div
                class="slider"
                role="region"
                aria-label={cnt.column_title || "Bild-Slider"}
                onmouseenter={stopAutoplay}
                onmouseleave={scheduleAutoplay}
            >
                <div class="slide-viewport">
                    {#each sliderImages as slide, idx}
                        <div class="slide" class:active={idx === activeSlide}>
                            {#if slide.link}
                                <a
                                    href={slide.link}
                                    target={resolveTarget(slide.link, slide.link_target)}
                                    rel="noopener noreferrer"
                                >
                                    <MedialibImage
                                        id={slide.image[0]}
                                        alt={`${cnt.column_title || "Slider-Bild"} ${idx + 1}`}
                                        maxWidth={900}
                                        modalable={false}
                                        disableFocusPosition={true}
                                    />
                                </a>
                            {:else}
                                <div class="image-wrapper">
                                    <MedialibImage
                                        id={slide.image[0]}
                                        alt={`${cnt.column_title || "Slider-Bild"} ${idx + 1}`}
                                        maxWidth={900}
                                        modalable={false}
                                        disableFocusPosition={true}
                                    />
                                </div>
                            {/if}
                        </div>
                    {/each}
                </div>

                {#if hasMultipleSlides}
                    <div class="controls">
                        <button class="scroll-button left" onclick={() => prevSlide()} aria-label="vorheriges Bild">
                            <img src="/media/leftChev.svg" alt="arrow-left" />
                        </button>
                        <ul class="dots" aria-label="Slider Navigation">
                            {#each sliderImages as _, idx}
                                <li class:active={idx === activeSlide}>
                                    <button
                                        onclick={() => {
                                            activeSlide = idx
                                            scheduleAutoplay()
                                        }}
                                        aria-label={`Bild ${idx + 1} anzeigen`}
                                    ></button>
                                </li>
                            {/each}
                        </ul>
                        <button class="scroll-button right" onclick={() => nextSlide()} aria-label="nächstes Bild">
                            <img src="/media/rightChev.svg" alt="arrow-right" />
                        </button>
                    </div>
                {/if}
            </div>
        {:else if fileLink}
            <VideoJs src={fileLink} controls={false} autoplay loop={cnt.loop !== false} muted preload="auto" />
        {/if}
    </div>
    <div class="text">
        <header>
            <h2>{cnt.column_title}</h2>
            <h3>{cnt.column_sub_title}</h3>
        </header>
        <section class="content">
            {#if cnt.description}
                {@html cnt.description}
            {/if}
        </section>

        <ul class="bold-numbers">
            {#each cnt.important_numbers as number}
                <li>
                    <em class="number">{number.number}</em>
                    <small>{number.description}</small>
                </li>
            {/each}
        </ul>

        <footer>
            <ul>
                {#if cnt.secondary_links}
                    {#each cnt.secondary_links as link}
                        <li>
                            <MoreButton
                                size={"big"}
                                text={link.text}
                                path={parseSecondaryLink(link.link)}
                                type="dark"
                                target={!link.target ? (openLinkInNewTab(link.link) ? "_blank" : "_self") : link.target}
                            />
                        </li>
                    {/each}
                {/if}
                {#if !cnt.disable_primary}
                    <li>
                        <MoreButton text={cnt.link_title} page_ref={cnt.page_link[0]} type="dark" size="big" />
                    </li>
                {/if}
            </ul>
        </footer>
    </div>
</section>

<style lang="less">
    @import "../../../../css/variables.less";
    .video-text-row {
        display: grid;
        align-items: stretch;
        width: 100%;
        gap: clamp(1.6rem, 3vw, 2.4rem);
        overflow: hidden;
        grid-template-columns: 1fr;
        padding: 0px var(--default-padding);

        @media @desktop_large {
            grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.9fr);
        }

        .media-card {
            position: relative;
            overflow: hidden;
            min-height: 320px;

            @media @max-mobile {
                min-height: 280px;
            }

            &.slider {
                display: flex;
            }

            .slider {
                position: relative;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: stretch;
                justify-content: flex-start;
                background: var(--bg-100);
            }

            .slide-viewport {
                position: relative;
                width: 100%;
                aspect-ratio: 1 / 1;
                flex-shrink: 0;
            }

            .slide {
                position: absolute;
                inset: 0;
                opacity: 0;
                transition: opacity 0.35s ease;
                pointer-events: none;
                &.active {
                    opacity: 1;
                    pointer-events: auto;
                }
                a,
                .image-wrapper {
                    width: 100%;
                    height: 100%;
                    display: block;
                    picture {
                        display: block;
                        width: 100%;
                        height: 100%;
                        img {
                            width: 100%;
                            height: 100%;
                            object-fit: contain;
                            object-position: center center;
                        }
                    }
                }
            }

            .controls {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;
                padding: 0.6rem 0.8rem;
                border-radius: 999px;
                @media @max-mobile {
                    position: static;
                    width: 100%;
                    margin-top: 1.2rem;
                    box-sizing: border-box;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-template-areas:
                        "dots dots"
                        "left right";
                    row-gap: 0.8rem;
                    column-gap: 0.8rem;
                    padding: 0;
                    align-items: center;
                }
                @media @tablet {
                    position: absolute;
                    left: 1.2rem;
                    right: 1.2rem;
                    bottom: 0;
                    z-index: 2;
                }

                .dots {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.4rem;
                    flex-wrap: wrap;
                    @media @max-mobile {
                        grid-area: dots;
                    }
                    li {
                        button {
                            width: 10px;
                            height: 10px;
                            border-radius: 999px;
                            background: var(--dark-20);
                            border: none;
                            cursor: pointer;
                            transition:
                                background 0.2s ease,
                                width 0.2s ease;
                        }
                        &.active button {
                            background: var(--brand-100);
                            width: 16px;
                        }
                    }
                }

                .scroll-button {
                    background: transparent;
                    @media @max-mobile {
                        width: 2.6rem;
                        height: 2.6rem;
                        padding: 0.5rem;
                    }
                    &.left {
                        @media @max-mobile {
                            grid-area: left;
                            justify-self: end;
                        }
                    }
                    &.right {
                        @media @max-mobile {
                            grid-area: right;
                            justify-self: start;
                        }
                    }
                }
            }

            :global(video) {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
        }

        .text {
            padding: 0px;

            width: 100%;

            display: flex;
            flex-direction: column;
            gap: 1.2rem;

            @media @desktop_large {
                grid-template-columns: 1fr 1fr;
                padding-top: 4.8rem;
                padding-bottom: 5.8rem;
            }
            header {
                display: flex;
                flex-direction: column;
                gap: 0.6rem;
            }
            .bold-numbers {
                border-top: 1px solid var(--dark-20);
                display: flex;
                gap: 1.2rem;
                padding-top: 1.2rem;
                @media @max-mobile {
                    flex-direction: column;
                }
                li {
                    display: flex;
                    flex-direction: column;
                    gap: 0.6rem;
                    em {
                        color: var(--brand-100);
                        font-style: normal;
                        font-size: 65px;
                        font-weight: 400;
                        line-height: 102%;
                    }
                }
                @media @max-mobile {
                    li {
                        align-items: center;
                    }
                }
            }
        }
    }
</style>
